/**
 * Place a pasted subgraph at an explicit canvas origin and optionally hang
 * its roots off a host mission. Used by "Paste Here" and (+) Paste Mission.
 */
import { copyIncomingDependencies } from '../../../FlowchartView/utils/parallelMission';
import {
  getAllConditionTaskIds,
  getDependencyTaskId,
  hasAnyConditions,
} from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

function isPasteWorkNode(task) {
  return Boolean(task) && task.nodeType !== 'note' && task.nodeType !== 'milestone';
}

function effectiveDependencyIds(task) {
  if (hasAnyConditions(task?.unlockConditions)) {
    return getAllConditionTaskIds(task.unlockConditions);
  }
  return (Array.isArray(task?.dependsOn) ? task.dependsOn : [])
    .map((dep) => getDependencyTaskId(dep))
    .filter(Boolean);
}

export function isWorkPasteRoot(task) {
  if (!task || task.nodeType === 'note' || task.nodeType === 'milestone') return false;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.length === 0;
}

export function placePastedTasksAtOrigin(tasks, origin) {
  if (!origin || !Number.isFinite(origin.x) || !Number.isFinite(origin.y)) {
    return tasks;
  }
  return (tasks || []).map((task) => {
    const rel = task._clipboardPos || { x: 0, y: 0 };
    return {
      ...task,
      x: origin.x + (Number.isFinite(rel.x) ? rel.x : 0),
      y: origin.y + (Number.isFinite(rel.y) ? rel.y : 0),
    };
  });
}

export function positionsFromPastedTasks(tasks) {
  const positions = {};
  (tasks || []).forEach((task) => {
    if (Number.isFinite(task?.x) && Number.isFinite(task?.y)) {
      positions[task.id] = { x: task.x, y: task.y };
    }
  });
  return positions;
}

export function attachPastedRootsToHost(tasks, hostTaskId, branch = null) {
  if (!hostTaskId) return tasks;
  const dep = branch ? { taskId: hostTaskId, branch } : hostTaskId;
  return (tasks || []).map((task) => {
    if (!isWorkPasteRoot(task)) return task;
    return { ...task, dependsOn: [dep] };
  });
}

function andBlock(taskIds) {
  return {
    version: 2,
    blocks: [{
      conditions: taskIds.map((taskId) => ({ taskId, branch: null })),
      innerOperator: 'AND',
      outerOperator: null,
    }],
  };
}

/**
 * Slip a pasted graph in front of a host. Roots inherit the host's incoming
 * formula. The host then depends only on the pasted leaves.
 */
export function insertPastedGraphBeforeHost(host, pastedTasks) {
  if (!host) return null;
  const pasted = pastedTasks || [];
  const pastedIds = new Set(pasted.map((task) => task.id));
  const work = pasted.filter(isPasteWorkNode);
  if (work.length === 0) return null;

  const dependsInsidePaste = (task) => (
    effectiveDependencyIds(task).some((id) => pastedIds.has(id))
  );
  const roots = work.filter((task) => !dependsInsidePaste(task));
  const referenced = new Set();
  work.forEach((task) => {
    effectiveDependencyIds(task).forEach((id) => {
      if (pastedIds.has(id)) referenced.add(id);
    });
  });
  const leaves = work.filter((task) => !referenced.has(task.id));
  const leafIds = leaves.map((task) => task.id);
  const rootIds = new Set((roots.length ? roots : work).map((task) => task.id));

  const nextPasted = pasted.map((task) => {
    if (!rootIds.has(task.id)) return task;
    const inputs = copyIncomingDependencies(host, host.id);
    const next = { ...task, dependsOn: inputs.dependsOn };
    if (inputs.unlockConditions) next.unlockConditions = inputs.unlockConditions;
    else delete next.unlockConditions;
    return next;
  });

  const hostPatch = {
    id: host.id,
    dependsOn: leafIds,
  };
  if (hasAnyConditions(host.unlockConditions) || leafIds.length > 1) {
    hostPatch.unlockConditions = andBlock(leafIds);
  }

  return {
    pastedTasks: nextPasted,
    hostPatch,
    centerTaskId: (roots[0] || leaves[0] || work[0]).id,
  };
}

export function applyHeldPositionsToFlowchart(flowchartRef, positions) {
  if (!positions || !flowchartRef?.current?.setTaskPosition) return;
  Object.entries(positions).forEach(([id, pos]) => {
    if (Number.isFinite(pos?.x) && Number.isFinite(pos?.y)) {
      flowchartRef.current.setTaskPosition(id, pos);
    }
  });
}
